/**
 * useFlowOptimizerTags — the computed Flow Optimizer ranking tags (optimizer
 * preview + rules, server-side) of a campaign, for the Paths / Landers /
 * Offers stats tabs.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';
import { matchTracker, type FlowLayer, type TrackerId } from '@trafficpulse/shared';

import api from '@/services/api';

export interface FlowOptimizerTagsResult {
  tagsByItemId: Record<string, string[]>;
  tagColors: Record<string, string>;
}

/**
 * Trackers the server computes ranking tags for. Tres Commas: none (the
 * endpoint answers empty for it, with or without its Flow Optimizer), so it
 * is never requested.
 */
function hasServerFlowTags(id: TrackerId): boolean {
  return matchTracker(id, {
    voluum: () => true,
    binom: () => true,
    'tres-commas': () => false,
  });
}

export function useFlowOptimizerTags(
  countryKey: string,
  campaignId: string | null | undefined,
  provider: TrackerId,
  layer: FlowLayer,
  enabled = true,
) {
  return useQuery({
    queryKey: ['smswiser', 'flow-optimizer-tags', countryKey, campaignId ?? '', provider, layer],
    enabled: enabled && !!campaignId && !!countryKey && hasServerFlowTags(provider),
    staleTime: 60_000,
    queryFn: async (): Promise<FlowOptimizerTagsResult> => {
      const res = await api.get<{ data?: FlowOptimizerTagsResult } & Partial<FlowOptimizerTagsResult>>(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/flow-optimizer-tags`,
        { params: { campaignId, provider, layer } },
      );
      const body = res.data?.data ?? res.data;
      return {
        tagsByItemId: body?.tagsByItemId ?? {},
        tagColors: body?.tagColors ?? {},
      };
    },
  });
}
